import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import FundPicker from './FundPicker';
import Unicon from '@/components/General/Unicon';

const centsToDollars = (cents) => (cents / 100).toFixed(2);
const formatCurrency = (text) => {
    const cleaned = String(text ?? '').replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    if (Number.isNaN(num)) return '';
    return num.toFixed(2);
};
const parseCents = (text) => {
    const cleaned = String(text ?? '').replace(/[^0-9.]/g, '');
    if (cleaned === '') return 0;
    const dollars = parseFloat(cleaned);
    if (Number.isNaN(dollars) || dollars < 0) return 0;
    return Math.round(dollars * 100);
};

function FundAmountInput({ amountCents, onUpdate, onBlurAmount, testID, appearance }) {
    const [text, setText] = useState(amountCents > 0 ? centsToDollars(amountCents) : '');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setText(amountCents > 0 ? centsToDollars(amountCents) : '');
        }
    }, [amountCents, isFocused]);

    const handleChangeText = (newText) => {
        const cleaned = newText.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        setText(cleaned);
        const cents = parseCents(cleaned);
        onUpdate?.(cents);
    };

    const handleBlur = () => {
        setIsFocused(false);
        const formatted = formatCurrency(text);
        setText(formatted);
        const cents = parseCents(formatted);
        onUpdate?.(cents);
        onBlurAmount?.(cents);
    };

    const displayValue = isFocused ? text : (amountCents > 0 ? `$${centsToDollars(amountCents)}` : '');

    return (
        <TextInput
            testID={testID}
            style={[styles.amountInput, appearance?.inputColor && { backgroundColor: appearance.inputColor }, appearance?.textColor && { color: appearance.textColor }]}
            value={displayValue}
            onChangeText={handleChangeText}
            onBlur={handleBlur}
            onFocus={() => setIsFocused(true)}
            keyboardType="decimal-pad"
            selectTextOnFocus
            placeholder="$0.00"
            placeholderTextColor={appearance?.textColor ? `${appearance.textColor}80` : styles.placeholder.color}
        />
    );
}

export default function FundSplitsEditor({ amountCents = 0, fundSplits = [], funds = [], onChange, testID = 'fund-splits-editor', appearance = null }) {
    const totalSplitCents = useMemo(() => fundSplits.reduce((sum, row) => sum + (row.amount || 0), 0), [fundSplits]);
    const isBalanced = totalSplitCents === amountCents;

    const updateRow = (index, updates) => {
        const next = fundSplits.map((row, i) => (i === index ? { ...row, ...updates } : row));
        onChange?.(next);
    };

    const addRow = () => {
        const remaining = Math.max(0, amountCents - totalSplitCents);
        const fallbackFund = funds.find((f) => !fundSplits.some((row) => row.fundID === f.id)) || funds[0];
        onChange?.([...fundSplits, { fundID: fallbackFund?.id, amount: remaining }]);
    };

    const removeRow = (index) => {
        const next = fundSplits.filter((_, i) => i !== index);
        onChange?.(next);
    };

    // With exactly 2 splits, blurring an amount auto-balances the OTHER row so
    // the two always sum to the gift total (e.g. $50 total, type $20 → other $30).
    const handleBlurAmount = (index, cents) => {
        if (fundSplits.length !== 2) return;
        if (cents < 0 || cents > amountCents) return;
        const otherIndex = index === 0 ? 1 : 0;
        const next = fundSplits.map((row, i) =>
            (i === otherIndex ? { ...row, amount: amountCents - cents } : row));
        onChange?.(next);
    };

    return (
        <View style={styles.container} testID={testID}>
            <Text style={[styles.title, appearance?.textColor && { color: appearance.textColor }]}>Fund Allocation</Text>
            {fundSplits.map((row, index) => (
                <View key={index} style={styles.row} testID={`${testID}-row-${index}`}>
                    <FundPicker
                        testID={`${testID}-row-${index}-fund`}
                        funds={funds}
                        selectedFundID={row.fundID}
                        onSelect={(fundID) => updateRow(index, { fundID })}
                        appearance={appearance}
                    />
                    <FundAmountInput
                        testID={`${testID}-row-${index}-amount`}
                        amountCents={row.amount || 0}
                        onUpdate={(cents) => updateRow(index, { amount: cents })}
                        onBlurAmount={(cents) => handleBlurAmount(index, cents)}
                        appearance={appearance}
                    />
                    {fundSplits.length > 1 && (
                        <Pressable
                            testID={`${testID}-row-${index}-remove`}
                            onPress={() => removeRow(index)}
                            style={styles.removeButton}
                        >
                            <Unicon name="close-circle" size={22} colorVariant="error" />
                        </Pressable>
                    )}
                </View>
            ))}
            <View style={styles.footer}>
                <Pressable testID={`${testID}-add-row`} onPress={addRow} style={styles.addRow}>
                    <Unicon name="add-circle-outline" size={18} color={appearance?.baseColor} colorVariant="giving" />
                    <Text style={[styles.addRowText, appearance?.baseColor && { color: appearance.baseColor }]}>Add another fund</Text>
                </Pressable>
                <Text style={[styles.total, appearance?.textColor && { color: `${appearance.textColor}B3` }, !isBalanced && styles.totalError]} testID={`${testID}-total`}>
                    ${centsToDollars(totalSplitCents)} of ${centsToDollars(amountCents)}
                </Text>
            </View>
            {!isBalanced && (
                <Text style={styles.error} testID={`${testID}-error`}>
                    Allocations must equal the total gift amount.
                </Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    container: {
        gap: spacing.sm,
    },
    title: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    amountInput: {
        width: 90,
        textAlign: 'right',
        fontSize: 16,
        fontFamily: fonts.body,
        color: colors.text,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
    },
    placeholder: {
        color: colors.textTertiary,
    },
    removeButton: {
        padding: spacing.xs,
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: spacing.xs,
    },
    addRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    addRowText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.giving,
    },
    total: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
    },
    totalError: {
        color: colors.error,
    },
    error: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.error,
    },
}));
